import KRGlue from '@lyracom/embedded-form-glue'
import AppContext, { useAppContext } from '../../../AppContextCheckout'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { billService } from '../../../../core/api'
import Loader from '../../../Loader'
import { useTranslation } from '../../../IntlContext/IntlContext'

const RenderPayzenForm = ({
	setSuccess,
	subscriptionId = '',
	couponId = '',
	gatewayConfig = { static_url: 'https://static.payzen.lat' },
	lang,
}) => {
	const componentId = useRef(Math.random().toString(36).substr(2, 9))
	
	const t = useTranslation()
	const { language } = useAppContext(AppContext)
	const { setAppState, payzenForm } = useAppContext(AppContext)
	
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState(false)
	const [isFormInitialized, setIsFormInitialized] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [currentInitializedLang, setCurrentInitializedLang] = useState(null)
	const autoTriggerExecuted = useRef(false)

	const setupPaymentForm = useCallback(async (selectedLang) => {
		const { form_token, public_key } = payzenForm

		if (!form_token || !public_key) {
			setError(true)
			return
		}

		if (!selectedLang) {
			return
		}

		try {
			setIsLoading(true)

			// Limpar formulário anterior se existir
			const formElement = document.getElementById('form-payzen')
			if (formElement) {
				formElement.innerHTML = '<div class="kr-smart-form" kr-card-form-expanded="true"></div>'
			}

			const endpoint = gatewayConfig.static_url
			const { KR } = await KRGlue.loadLibrary(endpoint, public_key)

			await KR.setFormConfig({
				formToken: form_token,
				language: selectedLang,
			}).then(({ KR }) => {
				const formElement = document.getElementById('form-payzen')
				if (formElement) {
					KR.removeForms()
					return KR.renderElements('#form-payzen')
				}
			})

			await KR.onSubmit(({ clientAnswer }) => {
				setIsSubmitting(true)
				
				if (clientAnswer.orderStatus === 'PAID') {
					setSuccess({
						done: true,
						checkoutResponse: { status: 200 },
						userInfo: {
							name: clientAnswer.customer?.billingDetails?.firstName || '',
							email: clientAnswer.customer?.email || '',
							totalPaid: {
								amount: clientAnswer.orderDetails?.orderPaidAmount || 0,
								currency: clientAnswer.orderDetails?.orderCurrency || 'BRL'
							}
						}
					})
				}
			})

			await KR.onError(async (e) => {
				const qs = new URLSearchParams(window.location.search)
				const hash = qs.get('hash')
				
				// Token expirado - tentar renovar
				if (e.errorCode === 'PSP_108') {
					try {
						const response = await billService.get(
							`/checkout/refresh?subscriptionId=${subscriptionId}${
								couponId ? `&couponId=${couponId}` : ''
							}&hash=${hash}`
						)
						
						const { form_token: newFormToken, public_key: newPublicKey } = response.data
						setAppState({ 
							payzenForm: { 
								form_token: newFormToken, 
								public_key: newPublicKey 
							} 
						})
						
						// Renderizar novamente com novo token
						setIsFormInitialized(false)
					} catch (err) {
						console.error('Error refreshing token:', err)
						setError(true)
					}
				} else {
					console.error('Payzen error:', e)
					setError(true)
				}
			})

			setIsFormInitialized(true)
			setCurrentInitializedLang(selectedLang)
			setIsLoading(false)
		} catch (error) {
			setIsLoading(false)
			setError(true)
			console.error('Error setting up payment form:', error)
		}
	}, [payzenForm, subscriptionId, couponId, setSuccess, setAppState, gatewayConfig.static_url])

	// Auto trigger para inicialização
	useEffect(() => {
		const currentLang = lang || language
		
		// Só inicializar se o idioma NÃO for pt-PT (que é o valor padrão do contexto)
		// Espera o idioma correto vir da API
		const isDefaultLang = currentLang === 'pt-PT'
		
		// Detectar mudança de idioma
		const langChanged = currentInitializedLang && currentInitializedLang !== currentLang
		
		if (langChanged && !isSubmitting) {
			// Idioma mudou - resetar e re-renderizar
			setIsFormInitialized(false)
			setCurrentInitializedLang(null)
			autoTriggerExecuted.current = false
			return
		}
		
		if (!autoTriggerExecuted.current && !isFormInitialized && payzenForm?.form_token && currentLang && !isDefaultLang) {
			autoTriggerExecuted.current = true
			setupPaymentForm(currentLang)
		}
	}, [payzenForm?.form_token, isFormInitialized, lang, language, setupPaymentForm, currentInitializedLang, isSubmitting])

	return (
		<>
			{isLoading && <Loader />}
			{error && (
				<div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
					{t.errors?.payzen_form_error || 'Erro ao carregar formulário de pagamento'}
				</div>
			)}
			{!isFormInitialized && !error && !isLoading && (
				<div style={{ textAlign: 'center', padding: '20px' }}>
					{t.messages?.loading || 'Carregando...'}
				</div>
			)}
		</>
	)
}

export default RenderPayzenForm
